import { useEffect, useMemo } from 'react'

import 'core-js/proposals/promise-with-resolvers'
import { ApolloProvider } from '@apollo/client'
import { ExtraErrorData } from '@sentry/integrations'
import { init, reactRouterV6Instrumentation, setTag, withProfiler } from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import ReactDOM from 'react-dom/client'
import { IntlProvider } from 'react-intl'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'
import { useRegisterSW } from 'virtual:pwa-register/react'

import acreConfig from '@acre/config'
import {
  Alert,
  SentryErrorBoundary,
  setDesignSystemConfig,
  theme,
  themeClarity,
  ThemeProvider,
} from '@acre/design-system'
import {
  createSentryBeforeSend,
  ErrorProvider,
  flattenMessages,
  getMessages,
  Locales,
  preloadCsClarityFont,
  setStorage,
  setUtilsConfig,
  useFlag,
} from '@acre/utils'

import '@babel/polyfill'
import './polyfills/pdfjs'

import App from './App'
import initApollo from './bootstrap/client'
import { InstallPromptProvider } from './contexts/InstallPromptContext'
import messages from './intl/messagesClientPortal'
import { getClientPortalMessages } from './main.helpers'

const flattenedMessages = flattenMessages(getMessages(messages, Locales.GB))

// `@acre/utils` imports no environment module of its own, so the application injects the
// environment specific values it needs before anything renders or issues a request.
setUtilsConfig({
  generalInsuranceRsaPublicKeyUrl: acreConfig.GENERAL_INSURANCE_RSA_PUBLIC_KEY_URL,
  protectionRsaPublicKeyUrl: acreConfig.PROTECTION_RSA_PUBLIC_KEY_URL,
  applicationRsaPublicKeyUrl: acreConfig.APPLICATION_RSA_PUBLIC_KEY_URL,
  refreshUrl: acreConfig.REFRESH_URL,
  clientOriginator: acreConfig.CLIENT_ORIGINATOR,
  globalFeatureFlags: acreConfig.GLOBAL_FEATURE_FLAGS,
})

// `@acre/design-system` imports no environment module of its own, so the application injects
// the environment specific values it needs before anything renders or issues a request.
setDesignSystemConfig({
  apiUrl: acreConfig.API_URL,
  mixpanelToken: acreConfig.MIXPANEL_TOKEN,
  sentryEnvironment: acreConfig.SENTRY_ENVIRONMENT,
  matomoBaseUrl: acreConfig.MATOMO_BASE_URL,
  matomoSiteId: acreConfig.MATOMO_SITE_ID,
})

// Temporary until the deprecated non-Oathkeepr API endpoints are fully removed (RD-14665).
// Seeds the Oathkeeper auth flag before render so the initial /me session check does not
// fall back to the non-OathKeeper API endpoint, which rejects a valid Ory session.
let oathKeeperSeedError: Error | undefined
setStorage(sessionStorage, 'useOathKeeper', 'true').catch((err) => {
  oathKeeperSeedError = err instanceof Error ? err : new Error(String(err))
})

// Allows us to use the sentry profiler, can track render times for the app component and descendants.
// Must stay at module scope: withProfiler returns a new component class per call, so building it
// inside a render would change the element type and remount the whole App subtree on every render.
const AppWithProfiler = withProfiler(App)

const ClientPortal = () => {
  useRegisterSW()

  const scoreFontCP = useFlag('feature.score_font_cp', false)

  useEffect(() => {
    if (oathKeeperSeedError) ErrorProvider.showError(oathKeeperSeedError)
  }, [])

  const client = useMemo(initApollo, [])
  const isScoreRebrandCP = useFlag('feature.score_rebrand_cp', false)
  const brandedMessages = useMemo(
    () => getClientPortalMessages(flattenedMessages, isScoreRebrandCP),
    [isScoreRebrandCP],
  )

  const isPlaywright = navigator.webdriver
  // @ts-expect-error - Cypress is a global variable
  const isCypress = window.Cypress

  // Disable sentry reporting in test environments
  if (import.meta.env.MODE === 'production' && !isCypress && !isPlaywright) {
    init({
      dsn: 'https://ee7d1ac2845d4d9a998f53da3575f68a@sentry.io/1876005',
      environment: acreConfig.SENTRY_ENVIRONMENT,
      integrations: [
        new BrowserTracing({
          routingInstrumentation: reactRouterV6Instrumentation(
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
          ),
        }),
        new ExtraErrorData({ depth: 5 }),
      ],
      tracesSampler: (samplingContext) => {
        if (samplingContext?.transactionContext?.op === 'navigation') return 0.015 // 1.5%
        // Apply a 0.15% sample rate for all other transactions (10× lower)
        return 0.0015
      },
      // We want to silence auth 401s as they are not genuine errors - they are the result of the user being logged out
      ignoreErrors: ['Invalid authorisation cookie'],
      beforeSend: createSentryBeforeSend({ team: 'cp', ignoreHttp401: true }),
    })
    setTag('issue_type', 'frontend')
    setTag('app', 'client-portal')
  }

  return (
    <ApolloProvider client={client}>
      <SentryErrorBoundary>
        <IntlProvider locale={Locales.GB} messages={brandedMessages}>
          <ThemeProvider theme={scoreFontCP ? themeClarity : theme}>
            <ErrorProvider alertSlot={Alert}>
              <InstallPromptProvider>
                <AppWithProfiler />
              </InstallPromptProvider>
            </ErrorProvider>
          </ThemeProvider>
        </IntlProvider>
      </SentryErrorBoundary>
    </ApolloProvider>
  )
}

if (!window.Intl) {
  // @ts-ignore
  require.ensure(['intl', 'intl/locale-data/jsonp/en.js'], (require) => {
    require('intl')
    require('intl/locale-data/jsonp/en.js')

    runApp()
  })
} else {
  runApp()
}

function runApp() {
  preloadCsClarityFont()

  ReactDOM.createRoot(document.getElementById('app') as HTMLElement).render(<ClientPortal />)
}
